import { useState } from "react";
import { Button, cn } from "@tellescope/ui";
import { useTranslation } from "react-i18next";
import { Plus, Trash2, GripVertical, Home } from "lucide-react";
import type { Page } from "../../../shared/schema";

interface PageListProps {
  pages: Page[];
  selectedPageId: string | null;
  onSelectPage: (pageId: string | null) => void;
  onAddPage: () => void;
  onRemovePage: (pageId: string) => void;
  onReorderPages: (pageIds: string[]) => void;
}

// Helper to reorder array elements
function arrayMove<T>(array: T[], fromIndex: number, toIndex: number): T[] {
  const result = [...array];
  const removed = result.splice(fromIndex, 1)[0];
  if (removed !== undefined) {
    result.splice(toIndex, 0, removed);
  }
  return result;
}

interface PageItemProps {
  page: Page;
  isFirst: boolean;
  isSelected: boolean;
  isDragging: boolean;
  canDelete: boolean;
  onSelect: () => void;
  onRemove: () => void;
  onDragStart: () => void;
  onDragOver: (e: React.DragEvent) => void;
  onDragEnd: () => void;
}

function PageItem({
  page,
  isFirst,
  isSelected,
  isDragging,
  canDelete,
  onSelect,
  onRemove,
  onDragStart,
  onDragOver,
  onDragEnd,
}: PageItemProps) {
  const { t } = useTranslation();

  return (
    <div
      draggable
      onDragStart={onDragStart}
      onDragOver={onDragOver}
      onDragEnd={onDragEnd}
      className={cn(
        "group flex items-center gap-1 rounded-md px-2 py-2 text-sm",
        isSelected ? "bg-primary text-primary-foreground" : "hover:bg-muted",
        isDragging && "opacity-50"
      )}
    >
      {/* Drag handle */}
      <div className="cursor-grab active:cursor-grabbing p-0.5 hover:bg-muted/50 rounded flex-shrink-0">
        <GripVertical
          className={cn("h-3.5 w-3.5", isSelected ? "text-primary-foreground/70" : "text-muted-foreground")}
        />
      </div>

      {/* Clickable area */}
      <div className="flex-1 flex items-center gap-2 cursor-pointer min-w-0" onClick={onSelect}>
        {isFirst && (
          <span title={t("Home page")}>
            <Home
              className={cn(
                "h-3 w-3 flex-shrink-0",
                isSelected ? "text-primary-foreground/70" : "text-muted-foreground"
              )}
            />
          </span>
        )}
        <span className="truncate">{page.title}</span>
      </div>

      {/* Delete button */}
      {canDelete && (
        <Button
          variant="ghost"
          size="sm"
          className={cn(
            "h-6 w-6 p-0 opacity-0 group-hover:opacity-100 flex-shrink-0",
            isSelected
              ? "hover:bg-primary-foreground/10 text-primary-foreground"
              : "hover:bg-destructive/10 hover:text-destructive"
          )}
          onClick={(e) => {
            e.stopPropagation();
            onRemove();
          }}
        >
          <Trash2 className="h-3 w-3" />
        </Button>
      )}
    </div>
  );
}

export const PageList: React.FC<PageListProps> = ({
  pages,
  selectedPageId,
  onSelectPage,
  onAddPage,
  onRemovePage,
  onReorderPages,
}) => {
  const { t } = useTranslation();

  // Track which page is being dragged
  const [draggedPageId, setDraggedPageId] = useState<string | null>(null);

  const handleDragStart = (pageId: string) => {
    setDraggedPageId(pageId);
  };

  const handleDragOver = (e: React.DragEvent, targetPageId: string) => {
    e.preventDefault();
    if (!draggedPageId || draggedPageId === targetPageId) return;

    const fromIndex = pages.findIndex((p) => p.id === draggedPageId);
    const toIndex = pages.findIndex((p) => p.id === targetPageId);
    if (fromIndex === -1 || toIndex === -1) return;

    // Reorder immediately for smooth feedback
    const newOrder = arrayMove(pages, fromIndex, toIndex);
    onReorderPages(newOrder.map((p) => p.id));
  };

  const handleDragEnd = () => {
    setDraggedPageId(null);
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-medium text-muted-foreground">{t("Pages")}</h3>
        <Button variant="ghost" size="sm" onClick={onAddPage}>
          <Plus className="h-4 w-4 mr-1" />
          {t("Add")}
        </Button>
      </div>

      <div className="space-y-1">
        {pages.map((page, index) => (
          <PageItem
            key={page.id}
            page={page}
            isFirst={index === 0}
            isSelected={selectedPageId === page.id}
            isDragging={draggedPageId === page.id}
            canDelete={pages.length > 1}
            onSelect={() => onSelectPage(selectedPageId === page.id ? null : page.id)}
            onRemove={() => onRemovePage(page.id)}
            onDragStart={() => handleDragStart(page.id)}
            onDragOver={(e) => handleDragOver(e, page.id)}
            onDragEnd={handleDragEnd}
          />
        ))}
      </div>

      {pages.length === 0 && (
        <p className="text-sm text-muted-foreground text-center py-4">{t("No pages yet. Add one to get started.")}</p>
      )}
    </div>
  );
};
